import type { NavigationFailure, RouteLocationRaw } from 'vue-router';

import router from './index';
import type { RouteConstant, RouteName, RouteParams, RoutesWithParams, TypedRouteLocation } from './routes';

type QueryValue = string | (string | null)[] | null | undefined;

export function routeLocation<T extends RouteConstant>(
  route: T,
  params?: Record<string, string | number>,
  options?: { query?: Record<string, QueryValue>; hash?: string },
): RouteLocationRaw {
  return {
    name: route.name,
    ...(params && { params }),
    ...(options?.query && { query: options.query }),
    ...(options?.hash && { hash: options.hash }),
  };
}

export function navigateTo<T extends RouteConstant>(
  route: T,
  params?: Record<string, string | number>,
  options?: { query?: Record<string, QueryValue>; hash?: string },
): Promise<void | NavigationFailure | undefined> {
  return router.push(routeLocation(route, params, options));
}

export function replaceTo<T extends RouteConstant>(
  route: T,
  params?: Record<string, string | number>,
  options?: { query?: Record<string, QueryValue>; hash?: string },
): Promise<void | NavigationFailure | undefined> {
  return router.replace(routeLocation(route, params, options));
}

export function hrefFor(location: RouteLocationRaw): string {
  return router.resolve(location).href;
}

export function hasRoute(name: RouteName): boolean {
  return router.hasRoute(name);
}

export function getCurrentRouteName(): RouteName | undefined {
  return router.currentRoute.value.name as RouteName | undefined;
}

export function isCurrentRoute(name: RouteName): boolean {
  return router.currentRoute.value.name === name;
}

export function buildRoute<T extends RouteName>(
  name: T,
  params?: T extends RoutesWithParams ? RouteParams[T] : never,
  options?: {
    query?: Record<string, QueryValue>;
    hash?: string;
  },
): TypedRouteLocation<T> {
  return {
    name,
    params,
    ...options,
  } as TypedRouteLocation<T>;
}

export function goBack(): void {
  router.back();
}

export function goForward(): void {
  router.forward();
}

export function go(delta: number): void {
  router.go(delta);
}
